import { Select, Input, Textarea } from '@fluentui/react-components';
import { CWWorkType } from '../services/cw-api';

export type BillableOption = 'Billable' | 'DoNotBill' | 'NoCharge';

interface TimeEntryFieldsProps {
  workTypes: CWWorkType[];
  selectedWorkType: number | undefined;
  setSelectedWorkType: (id: number) => void;
  timeHours: string;
  setTimeHours: (v: string) => void;
  billableOption: BillableOption;
  setBillableOption: (v: BillableOption) => void;
  timeNotes: string;
  setTimeNotes: (v: string) => void;
}

/**
 * Shared time-entry field set, rendered identically by OEL (TaskPane) and CML (MeetingPane)
 * so the two add-ins' time-entry mechanism cannot drift. Single source of truth for work type,
 * hours, the 3-way billable option (Billable / Do Not Bill / No Charge -> CW `billableOption`),
 * and time notes. Picking a work type seeds the billable option from that type's CW `billTime`
 * default. Extracted to close the CML 2-way-vs-OEL 3-way billable drift and keep parity by
 * construction going forward.
 */
export default function TimeEntryFields({
  workTypes,
  selectedWorkType,
  setSelectedWorkType,
  timeHours,
  setTimeHours,
  billableOption,
  setBillableOption,
  timeNotes,
  setTimeNotes,
}: TimeEntryFieldsProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', paddingLeft: '0.25rem' }}>
      {workTypes.length > 0 && (
        <Select
          value={String(selectedWorkType)}
          onChange={(_, d) => {
            const wtId = Number(d.value);
            setSelectedWorkType(wtId);
            const wt = workTypes.find(w => w.id === wtId);
            if (wt) setBillableOption((wt.billTime as BillableOption) || 'Billable');
          }}
          style={{ fontSize: '0.938rem' }}
        >
          {workTypes.map(wt => (
            <option key={wt.id} value={wt.id}>{wt.name || `Type ${wt.id}`}</option>
          ))}
        </Select>
      )}
      <Input
        type="number"
        placeholder="Hours (e.g. 0.25)"
        value={timeHours}
        onChange={(_, d) => setTimeHours(d.value)}
        step="0.25"
        min="0"
        style={{ fontSize: '0.938rem' }}
      />
      <Select
        value={billableOption}
        onChange={(_, d) => setBillableOption(d.value as BillableOption)}
        style={{ fontSize: '0.938rem' }}
      >
        <option value="Billable">Billable</option>
        <option value="DoNotBill">Do Not Bill</option>
        <option value="NoCharge">No Charge</option>
      </Select>
      <Textarea
        placeholder="Time notes (optional)"
        value={timeNotes}
        onChange={(_, d) => setTimeNotes(d.value)}
        resize="vertical"
        style={{ minHeight: '3rem', fontSize: '0.938rem' }}
      />
    </div>
  );
}
